import type { RowSelectionState } from "@tanstack/react-table";

/**
 * Range selection through the checkbox column of the grid.
 *
 * A plain click toggles a row and anchors it: the anchor remembers the state
 * the row got (checked or unchecked) and the selection at that moment (the
 * base). Shift+click rebuilds the selection from the base, applying the
 * anchor's state to every visible row between the anchor and the clicked row,
 * both included. Hence a second Shift+click from the same anchor recomputes
 * the range (to fix an overshoot) instead of accumulating, zones selected
 * before the anchor click stay as they were, and an anchor click that
 * unchecked its row turns Shift+click into a range uncheck.
 */

// Structural subset of TanStack's Row<Line>: the grid passes
// table.getRowModel().rows unchanged, tests use plain objects.
export interface RangeRow {
    id: string;
    depth: number;
    subRows: RangeRow[];
    original: { isLeftGrouped: boolean };
    getIsExpanded: () => boolean;
}

export interface RowRangeAnchor {
    // Row of the last plain click
    rowId: string;
    // Its state right after that click: the state a range gets
    selected: boolean;
    // Selection right after that click: every range is rebuilt on it
    base: RowSelectionState;
    // Selection object last written by a checkbox click. The anchor is only
    // usable while the store still holds this very object: a change made
    // elsewhere (select all, delete, view change...) replaces it.
    applied: RowSelectionState;
}

export interface RowCheckboxClick {
    rowId: string;
    shiftKey: boolean;
    anchor: RowRangeAnchor | null;
    // Visible record rows, in display order
    orderedRowIds: string[];
    // Current selection as held by the store (the reference matters)
    current: RowSelectionState;
}

export interface RowCheckboxClickResult {
    rowSelection: RowSelectionState;
    anchor: RowRangeAnchor;
}

/**
 * Lists the ids of the record rows currently rendered, in display order.
 * Record rows sit at `leafDepth` (the number of group levels); a vertical
 * group shows its rows only when expanded, a left-grouped row always shows
 * all of them.
 */
export function visibleRecordRowIds(rows: RangeRow[], leafDepth: number): string[] {
    const ids: string[] = [];
    const collect = (row: RangeRow) => {
        if (row.depth === leafDepth) {
            ids.push(row.id);
        } else if (row.depth < leafDepth) {
            if (row.original.isLeftGrouped) {
                row.subRows.forEach((child) => ids.push(child.id));
            } else if (row.getIsExpanded()) {
                row.subRows.forEach(collect);
            }
        }
    };
    rows.forEach(collect);
    return ids;
}

/**
 * Computes the selection and the anchor after a click on a row checkbox.
 * Inputs are never mutated.
 */
export function nextRowSelectionOnClick(click: RowCheckboxClick): RowCheckboxClickResult {
    const { rowId, shiftKey, anchor, orderedRowIds, current } = click;

    if (shiftKey && anchor && current === anchor.applied) {
        const anchorIndex = orderedRowIds.indexOf(anchor.rowId);
        const clickedIndex = orderedRowIds.indexOf(rowId);
        if (anchorIndex !== -1 && clickedIndex !== -1) {
            const rowSelection: RowSelectionState = { ...anchor.base };
            const from = Math.min(anchorIndex, clickedIndex);
            const to = Math.max(anchorIndex, clickedIndex);
            for (let i = from; i <= to; i++) {
                setSelected(rowSelection, orderedRowIds[i], anchor.selected);
            }
            return { rowSelection, anchor: { ...anchor, applied: rowSelection } };
        }
    }

    // Plain click, or Shift+click without a usable anchor: toggle the row and
    // anchor it
    const rowSelection: RowSelectionState = { ...current };
    const selected = !rowSelection[rowId];
    setSelected(rowSelection, rowId, selected);
    return { rowSelection, anchor: { rowId, selected, base: rowSelection, applied: rowSelection } };
}

// A deselected row is removed from the map: consumers count the keys
function setSelected(selection: RowSelectionState, rowId: string, selected: boolean): void {
    if (selected) {
        selection[rowId] = true;
    } else {
        delete selection[rowId];
    }
}
